import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Paper,
  Group,
  Text,
  Button,
  Table,
  Badge,
  Stack,
  Progress,
  Alert,
  Loader,
  Center,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { DollarSign, AlertTriangle, ExternalLink } from 'react-feather';
import { cohortBillcomApi } from '../../../../services/api/billcom';
import type { BillComPayment, PaymentJob } from '../../../../services/api/billcom';
import { formatCents, formatDateOnly } from '../../../../utils/formatters';
import {
  getPaymentNoteColor,
  getPaymentStatusConfig,
  HISTORY_ROW_STYLE,
  isHistoryPayment,
  STATUS_COLUMN_STYLE,
  WRAPPING_BADGE_STYLES,
} from '../../../../config/compensationConfig';
import { resolveBillcomBillUrl } from '../../../../config/env';

interface ScheduledPaymentsSectionProps {
  cohortId: string;
  canSchedule: boolean;
  hasUnmappedCoaches: boolean;
  /** MED-1383: the shared payment refresh key; a bump refetches this section. */
  refreshKey: number;
  /** MED-1383: called when a scheduling job ends so every payment view refetches. */
  onPaymentsChanged: () => void;
}

export function ScheduledPaymentsSection({
  cohortId,
  canSchedule,
  hasUnmappedCoaches,
  refreshKey,
  onPaymentsChanged,
}: ScheduledPaymentsSectionProps) {
  const [payments, setPayments] = useState<BillComPayment[]>([]);
  const [job, setJob] = useState<PaymentJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [scheduling, setScheduling] = useState(false);
  // Spinner on the first load for a cohort only; a refresh keeps the table on screen.
  const loadedCohortRef = useRef<string | null>(null);

  const fetchPayments = useCallback(async () => {
    try {
      if (loadedCohortRef.current !== cohortId) setLoading(true);
      // MED-1383: flat-rate rows and jobs only. Without the filter, Net 10 bills showed
      // here and hid the Schedule Payments button for a cohort with no flat-rate rows.
      const [paymentsData, jobData] = await Promise.all([
        cohortBillcomApi.getPayments(cohortId, { payment_track: ['flat_rate_monthly'] }),
        cohortBillcomApi.getLatestJob(cohortId, 'flat_rate_monthly'),
      ]);
      setPayments(paymentsData);
      setJob(jobData);
      loadedCohortRef.current = cohortId;
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments, refreshKey]);

  // Poll job progress while in progress
  useEffect(() => {
    if (!job || job.status !== 'in_progress') return;

    const interval = setInterval(async () => {
      try {
        const updated = await cohortBillcomApi.getJobProgress(cohortId, job.id);
        setJob(updated);
        if (updated.status !== 'in_progress') {
          clearInterval(interval);
          onPaymentsChanged();
        }
      } catch {
        clearInterval(interval);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [job, cohortId, onPaymentsChanged]);

  const runSchedulePayments = async () => {
    try {
      setScheduling(true);
      const result = await cohortBillcomApi.schedulePayments(cohortId);
      notifications.show({
        title: 'Payments Scheduling Started',
        message: `Scheduling ${result.total_payments} payments...`,
        color: 'blue',
      });
      // Fetch the job to start polling
      const jobData = await cohortBillcomApi.getJobProgress(cohortId, result.job_id);
      setJob(jobData);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to schedule payments';
      notifications.show({ title: 'Error', message: msg, color: 'red' });
    } finally {
      setScheduling(false);
    }
  };

  // MED-1383: scheduling creates every bill for the whole program at once. A coach
  // confirmed later is not added, and the button returns only after every bill of the
  // run is voided. Ask first, with the count and the total.
  const handleSchedulePayments = async () => {
    let preview;
    try {
      setScheduling(true);
      preview = await cohortBillcomApi.getSchedulePreview(cohortId);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Could not load the payment preview';
      notifications.show({ title: 'Error', message: msg, color: 'red' });
      return;
    } finally {
      setScheduling(false);
    }
    modals.openConfirmModal({
      title: 'Schedule payments in Bill.com',
      children: (
        <Stack gap="sm">
          <Text size="sm">
            This creates bills in Bill.com for <strong>{preview.coach_count}</strong> confirmed{' '}
            {preview.coach_count === 1 ? 'coach' : 'coaches'}.
          </Text>
          {/* MED-1383: the dates come from the preview, which computes them as the run
              does, so the dialog cannot disagree with what Schedule Payments creates. */}
          <Text size="sm">
            <strong>
              {preview.total_payments} {preview.total_payments === 1 ? 'payment' : 'payments'}
            </strong>
            , <strong>{formatCents(preview.total_cents)}</strong>
            {preview.first_payment_date &&
              (preview.last_payment_date &&
              preview.last_payment_date !== preview.first_payment_date
                ? `, monthly from ${formatDateOnly(preview.first_payment_date)} to ${formatDateOnly(
                    preview.last_payment_date,
                  )}`
                : `, on ${formatDateOnly(preview.first_payment_date)}`)}{' '}
            ({preview.program_months} {preview.program_months === 1 ? 'month' : 'months'}).
          </Text>
          {preview.runs_past_cohort_end && preview.cohort_end_date && (
            <Text size="sm" fw={500}>
              Group sessions run past the cohort end date (
              {formatDateOnly(preview.cohort_end_date)}). Check the session dates before you
              schedule.
            </Text>
          )}
          <Alert color="warningAmber" variant="light" icon={<AlertTriangle size={16} />}>
            Coaches who are not confirmed are not included. Coaches you confirm later are not
            added to this run, so confirm every coach first.
          </Alert>
        </Stack>
      ),
      labels: { confirm: 'Schedule payments', cancel: 'Cancel' },
      onConfirm: runSchedulePayments,
    });
  };

  const hasPayments = payments.length > 0;
  // MED-1383: after a re-run, the new live rows come first (process date, then group),
  // and the voided rows of the old run follow as history, in the same order.
  const sortedPayments = [...payments].sort(
    (a, b) =>
      Number(isHistoryPayment(a.status)) - Number(isHistoryPayment(b.status)) ||
      a.process_date.localeCompare(b.process_date) ||
      (a.line_items?.[0]?.group_name ?? '').localeCompare(
        b.line_items?.[0]?.group_name ?? '',
        undefined,
        { numeric: true },
      ),
  );
  // MED-1383: voided rows stay in the table as history, but they do not block a new
  // schedule. This matches the API check (hasPaymentsForCohort skips voided rows).
  const hasActivePayments = payments.some(p => p.status !== 'voided');
  const isJobRunning = job?.status === 'in_progress';
  const jobProgress = job ? Math.round(((job.completed_count + job.failed_count) / Math.max(job.total_payments, 1)) * 100) : 0;

  return (
    <Paper p="lg" radius="md" style={{ border: '1px solid #E8E6E1', background: 'white' }}>
      <Group justify="space-between" align="flex-start" mb="md">
        <div>
          <Text fw={700} size="lg">
            Scheduled Payments (Flat Rate)
          </Text>
          <Text size="sm" c="dimmed">
            Monthly flat-rate coach payments scheduled in Bill.com
          </Text>
        </div>
        {!hasActivePayments && canSchedule && (
          <Tooltip
            label="Link every coach to a Bill.com vendor before you schedule"
            disabled={!hasUnmappedCoaches}
          >
            {/* A disabled button fires no mouse events, so the span carries the tooltip. */}
            <span>
              <Button
                size="sm"
                onClick={handleSchedulePayments}
                loading={scheduling}
                disabled={hasUnmappedCoaches || isJobRunning}
              >
                Schedule Payments
              </Button>
            </span>
          </Tooltip>
        )}
      </Group>

      {/* Job Progress */}
      {isJobRunning && job && (
        <Alert color="blue" variant="light" mb="md" styles={{ root: { borderRadius: 8 } }}>
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Scheduling payments... {job.completed_count + job.failed_count} / {job.total_payments}
            </Text>
            <Progress value={jobProgress} size="sm" color="medleyGreen" radius="xl" animated />
            {job.failed_count > 0 && (
              <Text size="xs" c="dangerRed">
                {job.failed_count} failed
              </Text>
            )}
          </Stack>
        </Alert>
      )}

      {/* Job Errors */}
      {job?.status === 'completed_with_errors' && job.errors.length > 0 && (
        <Alert
          color="dangerRed"
          variant="light"
          icon={<AlertTriangle size={16} />}
          mb="md"
          styles={{ root: { borderRadius: 8 } }}
        >
          <Text size="sm" fw={500}>
            {job.failed_count} payment(s) failed to schedule
          </Text>
          <Text size="xs" c="dimmed" mt="xs">
            Successfully scheduled: {job.completed_count} / {job.total_payments}
          </Text>
        </Alert>
      )}

      {loading ? (
        <Center py="lg">
          <Loader size="sm" />
        </Center>
      ) : hasPayments ? (
        // MED-1383: on a narrow screen the table scrolls inside its card, not the page.
        <Table.ScrollContainer minWidth={720}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Coach</Table.Th>
                <Table.Th>Group</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Amount</Table.Th>
                <Table.Th>Process Date</Table.Th>
                <Table.Th style={STATUS_COLUMN_STYLE}>Status</Table.Th>
                <Table.Th style={{ textAlign: 'center' }}>Bill.com</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedPayments.map((p) => {
                const statusCfg = getPaymentStatusConfig(p.status, p.billcom_scheduled_date);
                return (
                  <Table.Tr
                    key={p.id}
                    style={isHistoryPayment(p.status) ? HISTORY_ROW_STYLE : undefined}
                  >
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {p.coach_name || 'Unknown'}
                      </Text>
                      {/* MED-1306: error_message was on the API type and rendered nowhere, so
                          a failed payment and a payment blocked in Bill.com both looked like
                          an ordinary wait. The approval poller writes the reason a bill cannot
                          be paid onto the row, and this is where a CM reads it.
                          MED-1383: a void is a normal state, so its note is gray, not red. */}
                      {p.error_message && (
                        <Text
                          size="xs"
                          c={getPaymentNoteColor(p.status)}
                          mt={2}
                          style={{ maxWidth: 360 }}
                        >
                          {p.error_message}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{p.line_items?.[0]?.group_name || '-'}</Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text size="sm" fw={600}>
                        {formatCents(p.amount_cents)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatDateOnly(p.process_date)}</Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}>
                      <Badge
                        size="sm"
                        color={statusCfg.color}
                        variant={statusCfg.variant}
                        styles={WRAPPING_BADGE_STYLES}
                      >
                        {statusCfg.label}
                      </Badge>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}>
                      {/* MED-1383: a hover tooltip, as on the other icon buttons. */}
                      {p.billcom_bill_id && (
                        <Tooltip label="View in Bill.com">
                          <a
                            href={resolveBillcomBillUrl(p.billcom_bill_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#2D4739' }}
                            aria-label="View in Bill.com"
                          >
                            <ExternalLink size={14} />
                          </a>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      ) : (
        <Stack align="center" py="xl" gap="xs">
          <DollarSign size={32} color="#C4C1BC" />
          <Text size="sm" c="dimmed" ta="center">
            No payments scheduled yet
          </Text>
          <Text size="xs" c="dimmed" ta="center" maw={400}>
            {canSchedule
              ? 'Schedule Payments creates the monthly bills for each confirmed coach with a flat rate. Coaches you confirm later are not added, so confirm every coach first.'
              : 'Confirm the cohort compensation and at least one coach to schedule payments.'}
          </Text>
        </Stack>
      )}
    </Paper>
  );
}
